import moment from "moment";
import { t } from "../i18n";
import debounce from "./debounce";
import parseIban from "./parseIban";
import { validateEmail, validatePhoneNumber } from "./pca";
const required = (value) => !value && value !== 0 && value !== false || value === `{}` ? t("Field is required") : void 0;
const number = (value) => !/^-?\d+\.?\d*$/.test(value) ? t("Field must be a number") : void 0;
const name = (value) => {
  const regex2 = /^[\w'\-,.][^0-9_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<>;:[\]]{1,}$/;
  if (!regex2.test(value)) {
    return t("Invalid name format");
  }
};
const email = (string) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  if (!re.test(string)) {
    return t("Invalid email address format");
  }
};
const address = (value) => {
  return !value || !value.Label ? t("Field is required") : void 0;
};
const isDateValid = (date2) => {
  const d = new Date(date2);
  if (Object.prototype.toString.call(d) === "[object Date]") {
    if (!Number.isNaN(Number(d.getTime()))) {
      return true;
    }
  }
};
const date = (validator) => (date2) => {
  const valid = isDateValid(date2);
  if (valid) {
    const pastTime = new Date(date2).getTime();
    const now = (/* @__PURE__ */ new Date()).getTime();
    const ellapsed = now - pastTime;
    const ellapsedDays = ellapsed / 1e3 / 60 / 60 / 24;
    const ellapsedYears = ellapsedDays / 365;
    return validator({ ellapsed, ellapsedDays, ellapsedYears });
  } else {
    return t("Invalid date");
  }
};
const simpleMemoize = (fn) => {
  let lastArg;
  let lastResult;
  return (arg) => {
    if (!lastArg || arg !== lastArg) {
      lastArg = arg;
      lastResult = fn(arg);
    }
    return lastResult;
  };
};
const emailPCA = simpleMemoize(
  debounce(async (value) => {
    const syntaxError = email(value);
    if (syntaxError) {
      return syntaxError;
    }
    const { Error: error, ResponseCode } = await validateEmail(value);
    if (!error && ResponseCode === "Invalid") {
      return t("Email address is not valid");
    }
  }, 800)
);
const phone = (value = "") => value === null || value.length < 11 ? t("Invalid phone number format") : void 0;
const mobilePhonePCA = simpleMemoize(
  debounce(async (value) => {
    const syntaxError = phone(value);
    if (syntaxError) {
      return syntaxError;
    }
    const {
      Error: error,
      IsValid,
      NumberType
    } = await validatePhoneNumber(value);
    if (!error && IsValid === "No") {
      return t("Phone number is not valid");
    }
    if (!["Mobile", "Unknown"].includes(NumberType)) {
      return t("Must be a mobile number");
    }
  }, 800)
);
const phonePCA = simpleMemoize(
  debounce(async (value) => {
    const syntaxError = phone(value);
    if (syntaxError) {
      return syntaxError;
    }
    const { Error: error, IsValid } = await validatePhoneNumber(value);
    if (!error && IsValid === "No") {
      return t("Phone number is not valid");
    }
  }, 800)
);
const minValue = (min) => (value) => (value || value === 0) && value < min ? t("Must be at least or equat to {{min}}", { min }) : void 0;
const maxValue = (max) => (value) => (value || value === 0) && value > max ? t(`Must be less or equal to {{max}}`, { max }) : void 0;
const valueRange = (min, max, customError) => (value) => {
  const error = customError || t("Must be between {{min}} and {{max}}", { min, max });
  if (value < min) {
    return error;
  }
  if (value > max) {
    return error;
  }
  return void 0;
};
const length = (target) => (value) => value.length !== target ? t(`Must be {{target}} characters`, { target }) : void 0;
const minLength = (min) => (value) => value && value.length < min ? t(`Must be {{min}} characters or more`, { min }) : void 0;
const maxLength = (max) => (value) => value && value.length > max ? t(`Must be {{max}} characters or less`, { max }) : void 0;
const lengthRange = (min, max, message) => (value) => {
  const error = message || t(`Length must be between {{min}} and {{max}} characters`, { min, max });
  if (value.length < min) {
    return error;
  }
  if (value.length > max) {
    return error;
  }
  return void 0;
};
const age = (min, max, errorMessage, unit = "years") => (value) => {
  const birthdate = moment(value);
  const yearDiff = moment().diff(birthdate, unit);
  if (yearDiff < min) {
    return errorMessage || t(`You must be over {{min}} to continue`, { min });
  }
  if (yearDiff > max) {
    return errorMessage || t(`You must be under {{max}} {{unit}} old to continue`, { max, unit });
  }
  return void 0;
};
const onlyContainsLetters = (value) => !/^[a-z]+$/i.test(value) ? t("Invalid input") : void 0;
const containsNumber = (value) => !value.match(/\d+/g) ? t("Invalid input") : void 0;
const containsUpperCase = (value) => !value.match(/(?=.*[A-Z])+/g) ? t("Invalid input") : void 0;
const containsLowerCase = (value) => !value.match(/(?=.*[a-z])+/g) ? t("Invalid input") : void 0;
const password = (value = "") => !containsNumber(value) && !containsLowerCase(value) && !containsUpperCase(value) && value.length > 7 ? void 0 : t("Invalid password");
const regex = (re, { errorMessage } = {}) => (value) => {
  if (!re.test(value)) {
    return errorMessage || t("Invalid input");
  }
};
const ibanNL = (v) => regex(/^NL\d{2}[A-Z]{4}\d{10}$/)(v);
const ibanFR = (v) => regex(/^FR\d{2}\d{5}\d{5}[A-Z0-9]{11}\d{2}$/)(v);
const ibanIT = (v) => regex(/^IT\d{2}[A-Z0-9]{1}\d{5}\d{5}\d{12}$/)(v);
const iban = (v) => {
  const result = parseIban(v);
  if (!result) {
    return t("Invalid input");
  }
};
export {
  address,
  age,
  containsLowerCase,
  containsNumber,
  containsUpperCase,
  date,
  email,
  emailPCA,
  iban,
  ibanFR,
  ibanIT,
  ibanNL,
  isDateValid,
  length,
  lengthRange,
  maxLength,
  maxValue,
  minLength,
  minValue,
  mobilePhonePCA,
  name,
  number,
  onlyContainsLetters,
  password,
  phone,
  phonePCA,
  regex,
  required,
  valueRange
};
